import { useCallback } from 'react';
import { ImoduleFilter } from 'types/global';
import { IrawSelectOption } from 'components/ModuleFilters/ModuleFiltersExpandableSelect';

type FocusedValue = string | number | boolean | null;

export type TMultiSelectFocusedValue = string[] | null;
export type TMultiSelectFocusedValueNumber = number[] | null;

interface IUseFilterClickHandlerOptionsBase {
  filterName: string;
  ordersFilters: ImoduleFilter[];
  setOrdersFilters: (filters: ImoduleFilter[]) => void;
  extractValue?: (option: IrawSelectOption) => string;
}

interface IUseFilterClickHandlerOptionsString extends IUseFilterClickHandlerOptionsBase {
  setFocusedValue: (value: string | null) => void;
  transformToFocusedValue?: (filterValue: string) => string | null;
}

interface IUseFilterClickHandlerOptionsNumber extends IUseFilterClickHandlerOptionsBase {
  setFocusedValue: (value: number | null) => void;
  transformToFocusedValue?: (filterValue: string) => number | null;
}

interface IUseFilterClickHandlerOptionsBoolean extends IUseFilterClickHandlerOptionsBase {
  setFocusedValue: (value: boolean | null) => void;
  transformToFocusedValue?: (filterValue: string) => boolean | null;
}

interface IUseFilterClickHandlerOptionsInternal extends IUseFilterClickHandlerOptionsBase {
  setFocusedValue: (value: FocusedValue) => void;
  transformToFocusedValue?: (filterValue: string) => FocusedValue;
}

function useFilterClickHandlerInternal(options: IUseFilterClickHandlerOptionsInternal) {
  const {
    filterName,
    ordersFilters,
    setOrdersFilters,
    setFocusedValue,
    transformToFocusedValue,
    extractValue
  } = options;

  return useCallback(
    (param: IrawSelectOption | string) => {
      const filterValue =
        extractValue && typeof param !== 'string'
          ? extractValue(param)
          : (typeof param === 'string' ? param : param.value || param.name || '');

      if (!filterValue) return;

      const focusedValue: FocusedValue = transformToFocusedValue
        ? transformToFocusedValue(filterValue)
        : filterValue;

      if (transformToFocusedValue && focusedValue === null) {
        return;
      }

      const currentFilters = [...ordersFilters];
      const filterIndex = currentFilters.findIndex(filter => filter.name === filterName);

      const isAlreadySelected =
        filterIndex >= 0 && currentFilters[filterIndex].values.includes(filterValue);

      if (isAlreadySelected) {
        currentFilters.splice(filterIndex, 1);
        setOrdersFilters(currentFilters);
        setFocusedValue(null);
      } else {
        if (filterIndex >= 0) {
          currentFilters[filterIndex] = {
            ...currentFilters[filterIndex],
            values: [filterValue]
          };
        } else {
          currentFilters.push({
            name: filterName,
            includes: 'in',
            values: [filterValue]
          });
        }
        setOrdersFilters(currentFilters);
        setFocusedValue(focusedValue);
      }
    },
    [filterName, ordersFilters, setOrdersFilters, setFocusedValue, transformToFocusedValue, extractValue]
  );
}

function useFilterClickHandlerString(
  options: IUseFilterClickHandlerOptionsString
): (param: IrawSelectOption | string) => void {
  return useFilterClickHandlerInternal({
    ...options,
    setFocusedValue: (value: FocusedValue) => {
      if (typeof value === 'string' || value === null) {
        options.setFocusedValue(value);
      }
    }
  });
}

function useFilterClickHandlerNumber(
  options: IUseFilterClickHandlerOptionsNumber
): (param: IrawSelectOption | string) => void {
  return useFilterClickHandlerInternal({
    ...options,
    setFocusedValue: (value: FocusedValue) => {
      if (typeof value === 'number' || value === null) {
        options.setFocusedValue(value);
      }
    }
  });
}

function useFilterClickHandlerBoolean(
  options: IUseFilterClickHandlerOptionsBoolean
): (param: IrawSelectOption | string) => void {
  return useFilterClickHandlerInternal({
    ...options,
    setFocusedValue: (value: FocusedValue) => {
      if (typeof value === 'boolean' || value === null) {
        options.setFocusedValue(value);
      }
    }
  });
}

interface IUseFilterClickHandlerOptionsStringMulti extends IUseFilterClickHandlerOptionsBase {
  setFocusedValue: (value: TMultiSelectFocusedValue) => void;
  getCurrentFocusedValue: () => string[] | string | null;
}

function useFilterClickHandlerStringMulti(
  options: IUseFilterClickHandlerOptionsStringMulti
): (param: IrawSelectOption | string) => void {
  const {
    filterName,
    ordersFilters,
    setOrdersFilters,
    setFocusedValue,
    getCurrentFocusedValue,
    extractValue
  } = options;

  return useCallback(
    (param: IrawSelectOption | string) => {
      let filterValue: string;
      if (typeof param === 'string') {
        filterValue = param;
      } else if (extractValue) {
        filterValue = extractValue(param);
      } else {
        const option = param;
        filterValue = option.value ?? option.name ?? '';
      }

      if (!filterValue) return;

      const current = getCurrentFocusedValue();
      const arr: string[] = Array.isArray(current)
        ? current
        : current != null && current !== ''
          ? [String(current)]
          : [];
      const isAlreadySelected = arr.includes(filterValue);
      const newArray = isAlreadySelected
        ? arr.filter(x => x !== filterValue)
        : [...arr, filterValue];
      const nextFocused: TMultiSelectFocusedValue = newArray.length > 0 ? newArray : null;
      setFocusedValue(nextFocused);

      const currentFilters = [...ordersFilters];
      const filterIndex = currentFilters.findIndex(filter => filter.name === filterName);

      if (nextFocused === null) {
        if (filterIndex >= 0) {
          currentFilters.splice(filterIndex, 1);
          setOrdersFilters(currentFilters);
        }
        return;
      }

      if (filterIndex >= 0) {
        currentFilters[filterIndex] = {
          ...currentFilters[filterIndex],
          values: nextFocused
        };
      } else {
        currentFilters.push({
          name: filterName,
          includes: 'in',
          values: nextFocused
        });
      }
      setOrdersFilters(currentFilters);
    },
    [filterName, ordersFilters, setOrdersFilters, setFocusedValue, getCurrentFocusedValue, extractValue]
  );
}

interface IUseFilterClickHandlerOptionsNumberMulti extends IUseFilterClickHandlerOptionsBase {
  setFocusedValue: (value: TMultiSelectFocusedValueNumber) => void;
  getCurrentFocusedValue: () => number[] | number | null;
  transformToFocusedValue?: (filterValue: string) => number | null;
  getFilterValuesFromFocused: (focused: number[]) => (string | number)[];
}

function useFilterClickHandlerNumberMulti(
  options: IUseFilterClickHandlerOptionsNumberMulti
): (param: IrawSelectOption | string) => void {
  const {
    filterName,
    ordersFilters,
    setOrdersFilters,
    setFocusedValue,
    getCurrentFocusedValue,
    transformToFocusedValue,
    extractValue,
    getFilterValuesFromFocused
  } = options;

  return useCallback(
    (param: IrawSelectOption | string) => {
      let filterValue: string;
      if (typeof param === 'string') {
        filterValue = param;
      } else if (extractValue) {
        filterValue = extractValue(param);
      } else {
        const option = param;
        filterValue = option.value ?? option.name ?? '';
      }

      if (!filterValue) return;

      const focusedNumber: number | null =
        transformToFocusedValue != null ? transformToFocusedValue(filterValue) : Number(filterValue);
      if (focusedNumber === null || Number.isNaN(focusedNumber)) return;

      const current = getCurrentFocusedValue();
      const arr: number[] = Array.isArray(current)
        ? current
        : current != null
          ? [current]
          : [];
      const isAlreadySelected = arr.includes(focusedNumber);
      const newArray = isAlreadySelected
        ? arr.filter(x => x !== focusedNumber)
        : [...arr, focusedNumber];
      const nextFocused: TMultiSelectFocusedValueNumber = newArray.length > 0 ? newArray : null;
      setFocusedValue(nextFocused);

      const currentFilters = [...ordersFilters];
      const filterIndex = currentFilters.findIndex(filter => filter.name === filterName);

      if (nextFocused === null) {
        if (filterIndex >= 0) {
          currentFilters.splice(filterIndex, 1);
          setOrdersFilters(currentFilters);
        }
        return;
      }

      const valuesToSet = getFilterValuesFromFocused(nextFocused);
      if (filterIndex >= 0) {
        currentFilters[filterIndex] = {
          ...currentFilters[filterIndex],
          values: valuesToSet
        };
      } else {
        currentFilters.push({
          name: filterName,
          includes: 'in',
          values: valuesToSet
        });
      }
      setOrdersFilters(currentFilters);
    },
    [
      filterName,
      ordersFilters,
      setOrdersFilters,
      setFocusedValue,
      getCurrentFocusedValue,
      transformToFocusedValue,
      extractValue,
      getFilterValuesFromFocused
    ]
  );
}

export {
  useFilterClickHandlerString,
  useFilterClickHandlerNumber,
  useFilterClickHandlerBoolean,
  useFilterClickHandlerStringMulti,
  useFilterClickHandlerNumberMulti
};
